import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Sparkles, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { AnamnesisSection } from "./components/AnamnesisSection";
import { PhysicalExamSection } from "./components/PhysicalExamSection";
import { DiagnosisSection } from "./components/DiagnosisSection";
import { TreatmentPlanSection } from "./components/TreatmentPlanSection";
import { TemplateSelector } from "./components/TemplateSelector";
import { PatientSelectorSheet } from "@/components/mealplan/PatientSelectorSheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ClinicalRecordFormData {
  patient_id: string | null;
  visit_date: string;
  visit_type: string;
  chief_complaint: string;
  anamnesis: {
    current_illness_history?: string;
    personal_history?: string;
    family_history?: string;
    allergies?: string;
    current_medications?: string;
    habits?: string;
  };
  physical_exam: {
    vital_signs?: {
      blood_pressure_systolic?: number;
      blood_pressure_diastolic?: number;
      heart_rate?: number;
      respiratory_rate?: number;
      temperature?: number;
      oxygen_saturation?: number;
      weight?: number;
      height?: number;
    };
    general_appearance?: string;
    head_neck?: string;
    cardiovascular?: string;
    respiratory?: string;
    abdomen?: string;
    musculoskeletal?: string;
    neurological?: string;
    skin?: string;
    other_findings?: string;
  };
  diagnosis: Array<{
    id?: string;
    code?: string;
    description: string;
    type: "primary" | "secondary";
  }>;
  treatment_plan: {
    conduct?: string;
    prescriptions?: string;
    exam_requests?: string;
    instructions?: string;
    follow_up?: string;
  };
  observations: string;
  status: string;
}

export default function ClinicalRecordCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const recordId = id || searchParams.get("edit") || null;

  const [selectedPatient, setSelectedPatient] = useState<{
    id: string;
    full_name: string;
    avatar_url?: string | null;
  } | null>(null);
  const [showPatientSelector, setShowPatientSelector] = useState(false);

  const [formData, setFormData] = useState<ClinicalRecordFormData>({
    patient_id: null,
    visit_date: format(new Date(), "yyyy-MM-dd"),
    visit_type: "consultation",
    chief_complaint: "",
    anamnesis: {},
    physical_exam: {},
    diagnosis: [],
    treatment_plan: {},
    observations: "",
    status: "draft",
  });

  // Carregar prontuário se estiver editando
  const { data: existingRecord, isLoading: isLoadingRecord } = useQuery({
    queryKey: ["clinical-record", recordId],
    queryFn: async () => {
      if (!recordId) return null;
      const { data, error } = await supabase
        .from("clinical_records")
        .select("*")
        .eq("id", recordId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!recordId,
  });

  useEffect(() => {
    if (existingRecord) {
      setFormData({
        patient_id: existingRecord.patient_id,
        visit_date: existingRecord.visit_date || format(new Date(), "yyyy-MM-dd"),
        visit_type: existingRecord.visit_type || "consultation",
        chief_complaint: existingRecord.chief_complaint || "",
        anamnesis: (existingRecord.anamnesis as ClinicalRecordFormData['anamnesis']) || {},
        physical_exam: (existingRecord.physical_exam as ClinicalRecordFormData['physical_exam']) || {},
        diagnosis: (((existingRecord.diagnosis as { records?: { code: string; description: string; type: string }[] })?.records) || []).map(d => ({
          ...d,
          type: (d.type === 'primary' || d.type === 'secondary') ? d.type : 'secondary' as const
        })),
        treatment_plan: (existingRecord.treatment_plan as ClinicalRecordFormData['treatment_plan']) || {},
        observations: existingRecord.observations || "",
        status: existingRecord.status || "draft",
      });

      // Carregar dados do paciente
      if (existingRecord.patient_id) {
        supabase
          .from("patients_onboarding")
          .select("id, full_name, avatar_url")
          .eq("id", existingRecord.patient_id)
          .single()
          .then(({ data }) => {
            if (data) setSelectedPatient(data);
          });
      }
    }
  }, [existingRecord]);

  const saveMutation = useMutation({
    mutationFn: async (status: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!formData.patient_id) throw new Error("Paciente é obrigatório");

      const recordData = {
        professional_id: user.id,
        patient_id: formData.patient_id,
        visit_date: formData.visit_date,
        visit_type: formData.visit_type,
        chief_complaint: formData.chief_complaint,
        anamnesis: formData.anamnesis,
        physical_exam: formData.physical_exam,
        diagnosis: { records: formData.diagnosis },
        treatment_plan: formData.treatment_plan,
        observations: formData.observations,
        status,
        completed_at: status === "completed" ? new Date().toISOString() : null,
      };

      if (recordId) {
        const { data, error } = await supabase
          .from("clinical_records")
          .update(recordData)
          .eq("id", recordId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
        .from("clinical_records")
          .insert(recordData)
        .select()
        .single();

      if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clinical-records"] });
      toast.success(
        recordId
          ? "Prontuário atualizado com sucesso!"
          : "Prontuário criado com sucesso!"
      );
      navigate(`/app/aplicativo/clinicalrecord/${data.id}`);
    },
    onError: (error: Error) => {
      toast.error("Erro ao salvar prontuário: " + error.message);
    },
  });

  const generateWithAIMutation = useMutation({
    mutationFn: async () => {
      if (!formData.chief_complaint.trim()) {
        throw new Error("Informe a queixa principal para gerar o prontuário");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("generate-clinical-record", {
        body: {
          chief_complaint: formData.chief_complaint,
          patient_id: formData.patient_id,
          visit_date: formData.visit_date,
          visit_type: formData.visit_type,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        setFormData((prev) => ({
          ...prev,
          anamnesis: data.anamnesis || prev.anamnesis,
          physical_exam: data.physical_exam || prev.physical_exam,
          diagnosis: data.diagnosis?.records || prev.diagnosis,
          treatment_plan: data.treatment_plan || prev.treatment_plan,
          observations: data.observations || prev.observations,
        }));
        toast.success("Prontuário gerado com sucesso pela IA!");
      }
    },
    onError: (error: Error) => {
      toast.error("Erro ao gerar prontuário: " + error.message);
    },
  });

  const suggestDiagnosisMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("suggest-clinical-diagnosis", {
        body: {
          chief_complaint: formData.chief_complaint,
          anamnesis: formData.anamnesis,
          physical_exam: formData.physical_exam,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.suggestions && Array.isArray(data.suggestions)) {
        const newDiagnoses = data.suggestions.map((suggestion: any, index: number) => ({
          description: suggestion.description || suggestion.diagnosis || "",
          code: suggestion.code || suggestion.cid10 || "",
          type: index === 0 ? "primary" : "secondary",
        }));
        setFormData((prev) => ({
          ...prev,
          diagnosis: [...prev.diagnosis, ...newDiagnoses],
        }));
        toast.success(`${newDiagnoses.length} diagnóstico(s) sugerido(s)!`);
      }
    },
    onError: (error: Error) => {
      toast.error("Erro ao sugerir diagnósticos: " + error.message);
    },
  });

  const handlePatientSelect = (patient: { id: string; full_name: string; avatar_url?: string | null; cpf?: string | null; email?: string | null }) => {
    setSelectedPatient({
      id: patient.id,
      full_name: patient.full_name,
      avatar_url: patient.avatar_url,
    });
    setFormData((prev) => ({ ...prev, patient_id: patient.id }));
    setShowPatientSelector(false);
  };

  const handleSave = (status: string) => {
    if (!formData.patient_id) {
      toast.error("Selecione um paciente");
      return;
    }
    saveMutation.mutate(status);
  };

  if (isLoadingRecord) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/app/aplicativo/clinicalrecord")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {recordId ? "Editar Prontuário" : "Novo Prontuário"}
          </h1>
          <p className="text-muted-foreground">
            {recordId
              ? "Edite as informações do prontuário"
              : "Preencha as informações do atendimento"}
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave("draft");
        }}
        className="space-y-6"
      >
        {/* Template Selector */}
        {!recordId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Template de Prontuário</CardTitle>
            </CardHeader>
            <CardContent>
              <TemplateSelector
                onSelectTemplate={(template) => {
                  setFormData((prev) => ({
                    ...prev,
                    chief_complaint: template.chief_complaint_template || prev.chief_complaint,
                    anamnesis: template.anamnesis_template || prev.anamnesis,
                    physical_exam: template.physical_exam_template || prev.physical_exam,
                    diagnosis: template.diagnosis_template?.records || prev.diagnosis,
                    treatment_plan: template.treatment_plan_template || prev.treatment_plan,
                    observations: template.observations_template || prev.observations,
                  }));
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Dados do Atendimento */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Dados do Atendimento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Paciente *</Label>
              {selectedPatient ? (
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Avatar>
                    <AvatarImage src={selectedPatient.avatar_url || ""} />
                    <AvatarFallback>
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">{selectedPatient.full_name}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPatientSelector(true)}
                  >
                    Alterar
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPatientSelector(true)}
                  className="w-full"
                >
                  <User className="h-4 w-4 mr-2" />
                  Selecionar Paciente
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="visit_date">Data do Atendimento *</Label>
                <Input
                  id="visit_date"
                  type="date"
                  value={formData.visit_date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, visit_date: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="visit_type">Tipo de Atendimento *</Label>
                <Select
                  value={formData.visit_type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, visit_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultation">Consulta</SelectItem>
                    <SelectItem value="follow_up">Retorno</SelectItem>
                    <SelectItem value="emergency">Emergência</SelectItem>
                    <SelectItem value="procedure">Procedimento</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chief_complaint">Queixa Principal *</Label>
              <div className="flex gap-2">
                <Textarea
                  id="chief_complaint"
                  placeholder="Descreva a queixa principal do paciente..."
                  rows={3}
                  value={formData.chief_complaint}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, chief_complaint: e.target.value }))
                  }
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => generateWithAIMutation.mutate()}
                  disabled={generateWithAIMutation.isPending || !formData.chief_complaint.trim()}
                  className="self-start"
                >
                  {generateWithAIMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Clique no ícone de IA para gerar o prontuário completo automaticamente
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Anamnese */}
        <AnamnesisSection
          data={formData.anamnesis}
          onChange={(anamnesis) =>
            setFormData((prev) => ({ ...prev, anamnesis }))
          }
        />

        {/* Exame Físico */}
        <PhysicalExamSection
          data={formData.physical_exam}
          onChange={(physical_exam) =>
            setFormData((prev) => ({ ...prev, physical_exam }))
          }
        />

        {/* Diagnóstico */}
        <DiagnosisSection
          diagnoses={formData.diagnosis}
          onChange={(diagnosis) =>
            setFormData((prev) => ({ ...prev, diagnosis }))
          }
          onSuggestDiagnosis={async () => {
            await suggestDiagnosisMutation.mutateAsync();
          }}
          isSuggesting={suggestDiagnosisMutation.isPending}
        />

        {/* Plano de Tratamento */}
        <TreatmentPlanSection
          data={formData.treatment_plan}
          onChange={(treatment_plan) =>
            setFormData((prev) => ({ ...prev, treatment_plan }))
          }
          patientId={formData.patient_id}
        />

        {/* Observações */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Observações</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Observações adicionais sobre o atendimento..."
              rows={4}
              value={formData.observations}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, observations: e.target.value }))
              }
            />
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app/aplicativo/clinicalrecord")}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSave("draft")}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar Rascunho
          </Button>
          <Button
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              handleSave("completed");
            }}
            disabled={saveMutation.isPending}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Finalizar Prontuário
          </Button>
        </div>
      </form>

      <PatientSelectorSheet
        open={showPatientSelector}
        onOpenChange={setShowPatientSelector}
        onSelectPatient={handlePatientSelect}
      />
    </div>
  );
}
